import React, { useEffect, useMemo, useState } from 'react';
import TrendCard from '../TrendCard';
import Ratings from '../Ratings';
import ReviewsProgress from './ReviewsProgress';
import MentionsCharts from './MentionsCharts';
import './index.scss';
import { getGameStoreStatsInfo, getGameStoreTimelineInfo, getStudioGameStore } from '@op/services';
import { arrayToObj, arrayToSimpleObj } from '@op/utils';
import { canRequest } from '../utils';
import { dealwithPcConsoleTimeline, dealwithTimelineList, defaultTimelineData } from '@op/pages/gamestore/utils';
import usePcConsoleTimelineChart from '@op/pages/gamestore/hooks/usePcConsoleTimelineChart';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import MobileGameStore from './MobileGameStore';
const seriesDataKeys = [
  {
    text: 'Positive',
    key: 'select_recommend',
    type: 'bar',
    beginColor: '#3286F3',
    endColor: '#4BD19C',
    valueKey: 'review',
  },
  {
    text: '30-Day Positive %',
    key: 'recent_reviews',
    type: 'line',
    color: '#5086F3',
    valueKey: 'score',
  },
  {
    text: 'Negative',
    key: 'select_non_recommend',
    type: 'bar',
    beginColor: '#AD53D4',
    endColor: '#F17C86',
    valueKey: 'review',
  },
];

type SteamItemType = { review: number; score: number; };
const getDefaultReview = () => ({ review: 0, score: 0 });


interface IProps {
  // 筛选器
  filter: any;
  // 竞品浮窗是否延时请求
  isDelay?: boolean;
  isFullscreen?: boolean;
  config?: { 
    // 竞品浮窗是否延时请求
    isDelay: boolean;
    className: string;
    style: React.CSSProperties;
  }; 
};

export default function GameStore(props: IProps) {

  const {filter, isDelay, config, isFullscreen } = props || {};
  const isDelayVal = isDelay ? isDelay : config?.isDelay;
  const { className, style } = config || {};
  const gameType = filter?.entity_type || "pc";

  const [steamInfo, setSteamInfo] = useState<{
    all_reviews: SteamItemType;
    recent_reviews: SteamItemType;
  }>({
    all_reviews: getDefaultReview(),
    recent_reviews: getDefaultReview(),
  });

  const [trendTimeline, setTrendsTimeline] = useState({
    list: [],
    timeline_of_end_time: '',
    timeline_of_start_time: '',
  });

  const [bottomGameStore, setBottomGameStore] = useState({});
  const [mobileGameStoreData, setMobileGameStoreData] = useState({});
  const [loading, setLoading] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);
  const [mobileTrendData, setMobileTrendData] = useState({});
  const [empty, setEmpty] = useState(true);

  // 获取Pc数据
  function getPcGameStoreData() {
    setLoading(true);
    getStudioGameStore(filter)
      .then((data: any) => {
        const { list, timeline_of_end_time, timeline_of_start_time, timeline } =
          data || {};
        const gameStoreMap = arrayToSimpleObj(list, 'list', 'channel');
        const localTrendsTimeline: any = dealwithPcConsoleTimeline(
          [{ timeline, channel: 'steam' }],
          timeline_of_start_time,
          timeline_of_end_time,
        );
        setTrendsTimeline(localTrendsTimeline.steam);
        const { steam, ...otherInfo } = gameStoreMap || {};
        setBottomGameStore(otherInfo);
        setSteamInfo(arrayToObj(steam, 'key'));
      }).catch(() => {}).finally(() => {
        setLoading(false);
      });
  }

  // 获取手游数据
  function getMobileGameStoreData() {
  // 接口传参
    const params = {
      ...(filter || {}),
      channel: ["app store", "google play"],
      types: ["lifetime", "selected"],
      only_date_filter: true,
    };
    setLoading(true);
    getGameStoreStatsInfo(params)
      .then((data: any) => {
        // 数据格式化成舆情组件需要的格式
        const formattedData: Record<string, any> = {};
    
        data.forEach((item: any) => {
          const { type, new_list } = item;
          if (!Array.isArray(new_list)) return;
    
          new_list.forEach((entry: any) => {
            const channel = entry.channel?.toLowerCase();
            if (!channel) return;
    
            if (!formattedData[channel]) {
              formattedData[channel] = {
                selected: [],
                lifetime: [],
              };
            }
    
            formattedData[channel][type] = entry;
          });
        });
    
        setMobileGameStoreData(formattedData);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  // 获取手游趋势图数据
  function getMobileTrendsData () {
    setTrendLoading(true);
    // setUserScoreTrendsApiError(false);
    getGameStoreTimelineInfo({
      ...filter,
      channel: ["app store", "google play"],
      types: ['lifetime', 'selected'],
    })
      .then((data: any) => {
        const globalTrendsTimeline = JSON.parse(JSON.stringify(defaultTimelineData()));
        const { date_type } = filter;
        const { timeline_list, timeline_of_end_time, timeline_of_start_time } =
            data || {};
        const globalTimelineMap: any = {
          'google play': { selected: [], lifetime: [] },
          'app store': { selected: [], lifetime: [] },
          taptap: { selected: [], lifetime: [] },
        };

        timeline_list?.map(({ type, list }: any) => {
          list
            ?.filter((item: { region: string; }) => item.region !== 'global')
            ?.forEach((item: { region: any; channel: any; }) => {
              if (item.region === '') {
                const newKey = item.channel;
                globalTimelineMap[newKey]?.[type]?.push(item);
              }
            });
        });
  
        for (const key in globalTimelineMap) {
          const { selected, lifetime } = globalTimelineMap[key] || {
            selected: [],
            lifetime: [],
          };
          globalTrendsTimeline[key] = dealwithTimelineList(
            [
              { type: 'selected', list: selected },
              { type: 'lifetime', list: lifetime },
            ],
            timeline_of_start_time,
            timeline_of_end_time,
            date_type,
          );
        }
        setMobileTrendData(globalTrendsTimeline);
      })
      .finally(() => setTrendLoading(false));
  };

  useEffect(() => {
    if (canRequest(filter)) {
      if(gameType === "mobile"){
        getMobileGameStoreData();
        getMobileTrendsData ();
      }
      if(gameType === "pc"){
        getPcGameStoreData();
      }
    }
  }, [filter, gameType]);

  const { chartMap } = usePcConsoleTimelineChart({
    platformData: trendTimeline,
    filterParams: filter,
  });

  const steamInfoEmpty = useMemo(() => {
    if (!steamInfo) return true;
    const { all_reviews, recent_reviews } = steamInfo;
    if (!all_reviews && !recent_reviews) return true;
    if (
      all_reviews?.review === 0 &&
      recent_reviews?.review === 0 &&
      all_reviews?.score === 0 &&
      recent_reviews?.score === 0
    ) {
      return true;
    }
    return seriesDataKeys.every(({ key, valueKey }) => {
      const data = chartMap[key];
      if (!data || data?.length === 0) return true;
      return data.every((item) => item?.[valueKey] === 0);
    });
  }, [steamInfo]);

  // 判断是否无数据
  useEffect(() => {
    if(gameType === "mobile"){
      setEmpty(!mobileGameStoreData);
    }
    if(gameType === "pc"){
      setEmpty(steamInfoEmpty);
    }

  }, [mobileGameStoreData, steamInfoEmpty,gameType]);

  return (
    <div className={className} style={style}>
      <TrendCard
        loading={gameType === 'mobile' ? (loading || trendLoading) : loading}
        loadingHeight={264}
        moduleType="game_store"
        filter={filter}
        showRank ={gameType !== "mobile"}
        empty={empty}
        emptyHeight={264}
        hasEmptyDesc={false}
        formatValue={(value: string | number) => dataFormat({ value, formatRule: 'bmk' })}
        className="dbh-trend-card--gamestore"
        title={'Game Store'}
        isDelay={isDelayVal}
        urlKey={gameType === "mobile"? 'Game Store Mobile' : 'Game Store'}
      >
        {gameType === 'mobile' ? (
          <MobileGameStore filter={filter} data={mobileGameStoreData} trendsData={mobileTrendData} />
        ) : (
          <div className="dbh-gamestore-box">
            <div className="dbh-gamestore-box-left">
              <ReviewsProgress
                data={steamInfo.all_reviews || getDefaultReview()}
                title="All Reviews"
              />
              <ReviewsProgress
                data={steamInfo.recent_reviews || getDefaultReview()}
                title="Recent Reviews"
              />
            </div>
            <div className="dbh-gamestore-box-right">
              <MentionsCharts
                chartMap={chartMap}
                filter={filter}
                seriesDataKeys={seriesDataKeys}
                isFullscreen={isFullscreen}
              />
              <div className="dbh-gamestore-ratings-box">
                <Ratings data={bottomGameStore} />
              </div>
            </div>
          </div>
        )}
      </TrendCard>
    </div>
  );
}
